import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarIcon } from "lucide-react";
import { DATE_PRESETS, toInputDate } from "@/lib/dateRange";

/**
 * Compact date range filter used on Calls & Leads pages.
 * Props:
 *   preset, onPresetChange, customStart, customEnd, onCustomChange
 */
export function DateRangeFilter({ preset, onPresetChange, customStart, customEnd, onCustomChange, testId = "date-range" }) {
  const [open, setOpen] = useState(false);
  const currentLabel = DATE_PRESETS.find((p) => p.value === preset)?.label || "All time";

  const applyPreset = (v) => {
    onPresetChange(v);
    if (v !== "custom") setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-2" data-testid={`${testId}-trigger`}>
          <CalendarIcon className="w-3.5 h-3.5" />
          <span className="text-xs">{currentLabel}</span>
          {preset === "custom" && customStart && customEnd && (
            <span className="text-[10px] text-muted-foreground font-mono">
              {toInputDate(customStart)} → {toInputDate(customEnd)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2" data-testid={`${testId}-popover`}>
        <div className="grid grid-cols-2 gap-1">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => applyPreset(p.value)}
              data-testid={`${testId}-preset-${p.value}`}
              className={`text-left text-xs px-2.5 py-1.5 rounded-md transition-colors ${
                preset === p.value
                  ? "bg-primary/15 text-foreground font-semibold"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">From</Label>
              <Input
                type="date"
                value={toInputDate(customStart)}
                onChange={(e) => onCustomChange({ start: e.target.value ? new Date(e.target.value) : null, end: customEnd })}
                data-testid={`${testId}-custom-start`}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">To</Label>
              <Input
                type="date"
                value={toInputDate(customEnd)}
                onChange={(e) => onCustomChange({ start: customStart, end: e.target.value ? new Date(e.target.value) : null })}
                data-testid={`${testId}-custom-end`}
                className="h-8"
              />
            </div>
            <Button size="sm" className="w-full h-8" onClick={() => setOpen(false)} data-testid={`${testId}-apply`}>Apply</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
